/**
 * Dialog for confirming undo of work order status change
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';
import { ResponsiveDialog } from '../common/ResponsiveDialog';

interface UndoConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  oldStatus: string;
  newStatus: string;
  isTerminalState?: boolean;
}

export function UndoConfirmDialog({
  open,
  onClose,
  onConfirm,
  oldStatus,
  newStatus,
  isTerminalState = false,
}: UndoConfirmDialogProps) {
  const { t } = useTranslation('workorders');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError(t('undoReasonRequired'));
      return;
    }

    onConfirm(reason.trim());
    setReason('');
    setError('');
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      title={isTerminalState ? t('reopenWorkOrder') : t('undoConfirmTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        {isTerminalState ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('reopenConfirm')}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('undoConfirmMessage', {
              oldStatus: t(`status.${oldStatus}`),
              newStatus: t(`status.${newStatus}`),
            })}
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('undoReason')}
          placeholder={t('undoReasonPlaceholder')}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError('');
          }}
          error={!!error}
          helperText={error}
          required
          autoFocus
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {t('cancel', { ns: 'common' })}
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          {t('undo')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
